import type {
  OnboardingStepContext,
  ResolveResult,
} from "@/pages/onboarding/stepMachine/onboardingStepContext";
import { stepToViewStep, type Step } from "@/pages/onboarding/stepMachine/steps";
import {
  isCertBeforeDgi as isCertBeforeDgiFor,
  isNewCertFlow as isNewCertFlowFor,
} from "@/pages/onboarding/certFlowOrdering";
import { isCertRequestInFlight } from "@/pages/onboarding/certificatePhase";

/**
 * Clean reimplementation of the load-time step reconstruction (the ~400-line
 * step-sync effect), as a pure function. Same output as `resolveStepLegacy`
 * (asserted branch-for-branch by the golden-master) but organized by path family
 * and expressed via semantic `Step` names instead of magic integers.
 *
 * `viewStep` stays the public wire value: targets are projected with
 * `stepToViewStep`. The one genuinely integer-shaped case — the (pre-empted,
 * dead) settled-profile accountant-owner `prev<=5?5:prev` branch — keeps a raw
 * integer, since it is not a nameable step.
 */
export function resolveStep(ctx: OnboardingStepContext): ResolveResult {
  const at = (step: Step): number => stepToViewStep(step, ctx);

  // --- waiting / not-ready ---
  if (ctx.loading) return { synced: false };
  if (!ctx.hasTenant) return { synced: false };
  if (ctx.onboardingMode == null) return { step: at("intro"), synced: true };

  // --- accountant-flow owner: régimen -> plan gate, then handoff ---
  if (isAccountantOwner(ctx)) {
    if (ctx.onboardingCompletedAt || ctx.planId) return { handoff: true };
    return {
      step: at(ctx.fiscalBenefitDeclared ? "plan_gate" : "regime"),
      synced: true,
    };
  }

  // --- self path: régimen (2) then plan (3) precede situation ---
  // The declaration and plan are chosen before anything else; once `planId` is
  // persisted this arm is skipped and the situation/dgi/post-cert routing below
  // runs at the shifted integers. `fiscalBenefitDeclared` is the durable pivot
  // (survives reload), so refresh reconstructs the right pre-plan step.
  if (
    ctx.onboardingMode === "self" &&
    !ctx.onboardingCompletedAt &&
    !ctx.planId
  ) {
    return {
      step: at(ctx.fiscalBenefitDeclared ? "plan_gate" : "regime"),
      synced: true,
    };
  }

  if (ctx.onboardingSituationChoice == null) {
    return { step: at("situation"), synced: true };
  }

  // --- fiscal-profile query still pending: resolve the pre-DGI region only ---
  if (!ctx.isFiscalProfileSettled) {
    if (isDgiDone(ctx)) return {}; // nothing to route yet; wait for the settled pass
    // Mirror the settled path: a cert-first tenant whose cert is already on file
    // belongs on dgi_access, not the cert sub-flow. Without this the unsettled
    // pass briefly routes through preDgiCertRouting -> cert_choice and the
    // settled pass then corrects to dgi_access, producing a ~0.4s flash.
    const certOnFile = ctx.hasDgiCredentials || ctx.dgiCertificateSkippedAt;
    if (isCertBeforeDgi(ctx) && !certOnFile) return preDgiCertRouting(ctx, at);
    return { step: at("dgi_access"), synced: true };
  }

  const wasLoading = ctx.wasLoading;

  // NOTE: the original effect had a settled-profile accountant-owner block here
  // (navigate / acc_owner_invite / steps / plan / success). It was unreachable —
  // the top-of-function accountant-owner check returns unconditionally for the
  // same guard — so it is omitted (confirmed by the golden-master: no
  // accountant-owner input ever produced those outputs).

  // --- accountant user ---
  if (ctx.userRole === "accountant" && ctx.onboardingMode === "accountant") {
    if (!wasLoading) return { synced: true };
    if (!ctx.s1Complete) return { step: at("acc_user_company"), synced: true };
    if (!ctx.literalEComplete) return { step: at("acc_user_literal_e"), synced: true };
    if (!ctx.hasDgiCredentials) return { step: at("acc_user_cert"), synced: true };
    return { step: at("acc_user_success"), synced: true };
  }

  // --- self / non-accountant ---
  const certAlreadyOnFile = ctx.hasDgiCredentials || ctx.dgiCertificateSkippedAt;

  // Scratch resumes after the team finishes homologación: the DGI phase is done,
  // so skip the DGI pin / celebration / complete gates and route straight into
  // the post-DGI cert tail (postDgiRouting sends to cert, then CAE).
  if (ctx.onboardingSituationChoice === "scratch" && ctx.homologacionCompleted) {
    return postDgiRouting(ctx, at);
  }

  // Scratch tenants are pinned to DGI until the portal user is real.
  if (
    ctx.onboardingSituationChoice === "scratch" &&
    !(ctx.hasDgiPortalUser && dgiPortalUserConfirmedForStep(ctx))
  ) {
    return { step: at("dgi_access"), synced: true };
  }

  // A cert on file may bypass the DGI-access gate ONLY when DGI precedes the
  // cert (DGI-first, e.g. already_issuer): there, credentials on file imply the
  // portal user already exists. For cert-first regularization the cert is
  // acquired BEFORE the portal user, so cert-on-file says nothing about DGI —
  // the tenant must still land on (and confirm, auto or manual) the DGI
  // portal-user step instead of being dropped on CAE with no way forward.
  const certImpliesDgiDone = certAlreadyOnFile && !isCertBeforeDgi(ctx);

  if (!isDgiAccessStepComplete(ctx) && !certImpliesDgiDone) {
    // Cert-first: run the cert sub-flow only while the cert is not yet on file;
    // once acquired, fall through to the DGI portal-user step.
    if (isCertBeforeDgi(ctx) && !certAlreadyOnFile) {
      return preDgiCertRouting(ctx, at);
    }
    return { step: at("dgi_access"), synced: true };
  }

  // Keep the celebration screen until the user explicitly continued.
  if (!ctx.step3ContinuedMarker && !certAlreadyOnFile) {
    return { step: at("dgi_access"), synced: true };
  }

  return postDgiRouting(ctx, at);
}

function isAccountantOwner(ctx: OnboardingStepContext): boolean {
  return (
    ctx.onboardingMode === "accountant" &&
    (ctx.userRole === "owner" || ctx.userRole === "admin")
  );
}

/**
 * Cert-first ORDERING (regularization only). Drives `preDgiCertRouting`: scratch
 * is DGI-first, so it is deliberately NOT included here — the scratch-pin +
 * post-DGI routing carry its DGI-first order.
 */
function isCertBeforeDgi(ctx: OnboardingStepContext): boolean {
  return isCertBeforeDgiFor(ctx.onboardingSituationChoice);
}

/**
 * A fresh certificate is being acquired (scratch OR regularization). Drives the
 * post-DGI soft-cert exemption so both can advance past cert to CAE before the
 * credentials are physically in hand.
 */
function isNewCertFlow(ctx: OnboardingStepContext): boolean {
  return isNewCertFlowFor(ctx.onboardingSituationChoice);
}

function isDgiDone(ctx: OnboardingStepContext): boolean {
  return (
    ctx.hasDgiPortalUser &&
    (ctx.dgiPortalUserConfirmed ||
      ctx.dgiPortalUserConfirmedAt ||
      ctx.autoFlowPortalUserStamped)
  );
}

function dgiPortalUserConfirmedForStep(ctx: OnboardingStepContext): boolean {
  return (
    ctx.dgiPortalUserConfirmed ||
    ctx.dgiPortalUserConfirmedAt ||
    ctx.autoFlowPortalUserStamped
  );
}

function isDgiAccessStepComplete(ctx: OnboardingStepContext): boolean {
  return (
    (ctx.hasDgiPortalUser &&
      dgiPortalUserConfirmedForStep(ctx) &&
      ctx.dgiStepsCompletedConfirmed &&
      ctx.dgiStep3SuccessToastAcknowledged) ||
    ctx.step3ContinuedMarker
  );
}

/**
 * The tenant's real position is the Abitab step: they chose Fiscalo-managed
 * acquisition, we filed an ID Digital request for them, the certificate is not
 * on file yet, and they have not clicked past the acquisition sub-flow.
 *
 * Until this existed, resolveStep had no arm that could return `abitab` at all
 * — steps.ts numbered it and OnboardingStepContent rendered it, but the only
 * way in was clicking forward from cert_content, so every refresh dropped the
 * tenant back a screen. That screen is the one carrying the solicitud number
 * they hand over at the Abitab counter, so losing it is not cosmetic.
 *
 * postDgiRouting used to carry a comment calling this case unreachable, citing
 * decision 0a59dad. That verdict was reached 2026-08-27 against the old flow,
 * before Fiscalo-managed cert requests existed; the durable request record is
 * what makes it reachable now.
 *
 * `releaseOnAcquisitionDone` is asymmetric between the two orderings, and the
 * asymmetry is the whole point:
 *
 *  - CERT-FIRST (preDgiCertRouting, regularization) passes true. There, the
 *    acquisition marker is how a tenant who has been to Abitab proceeds to the
 *    DGI phase. Their certificate does not exist until the payment clears, so
 *    without this release the guard would pin them on the Abitab step and they
 *    could never reach DGI at all.
 *  - DGI-FIRST (postDgiRouting, e.g. already_issuer) passes false. There the
 *    marker means only "clicked past the cert-content screen", and the tenant
 *    still has to go to Abitab and pay. Honouring it there sent them to
 *    cert_content — which is what happened in production on 2026-09-29: a
 *    tenant with onboardingCertAcquisitionDoneAt set and request 2745063 in
 *    flight refreshed and lost the screen carrying their solicitud number,
 *    which is the entire bug this guard exists to fix.
 *
 * The newCertFlow arm above already releases DGI-first tenants by a different
 * route — once certContentDone is set they leave this arm for the post-cert
 * tail — so dropping the clause here does not strand anyone.
 */
function isAwaitingAbitab(
  ctx: OnboardingStepContext,
  { releaseOnAcquisitionDone }: { releaseOnAcquisitionDone: boolean },
): boolean {
  const choice = ctx.certChoiceMarker ?? ctx.certificateAcquisitionChoice;
  // Stated in both places on purpose. The arm above already exits on this, so
  // this line is redundant TODAY — and that is the point: two conditions that
  // must agree, with nothing connecting them, is the shape that produced three
  // separate bugs on 2026-09-29.
  if (ctx.certWaitContinuedMarker) return false;
  if (releaseOnAcquisitionDone && ctx.certAcquisitionDoneMarker) return false;
  return (
    choice === "fiscalo" &&
    isRequestHoldingTenantAtAbitab(ctx) &&
    !ctx.hasDgiCredentials &&
    !ctx.dgiCertificateSkippedAt
  );
}

/**
 * Whether the certificate request is in a state whose screen lives ON the
 * Abitab step.
 *
 * `fulfilled` counts, which is not obvious: the certificate exists, so it reads
 * like a finished state. It belongs here because of where the exits are.
 *
 * On the new-cert flow — the tenants this routing is actually for — the Abitab
 * step's Next gate (transitions.ts, the newCertFlow arm of 6 + off) blocks only
 * on a null or failed request, so a fulfilled tenant can move on; and if they
 * never pressed "Ya fui a Abitab", the step still offers that button.
 * cert_content offers neither: its gate (step5FiscaloCanAdvance) enables for
 * pending, in_progress and failed but NOT fulfilled, so sending a fulfilled
 * tenant there can strand them with no way forward and no way back.
 *
 * Legacy (non-newCertFlow) tenants are gated on credentials arriving at BOTH
 * screens, so this choice neither helps nor hurts them.
 *
 * The same reasoning settles a status this union has not caught up with: the
 * Abitab gate lets anything that is not null or failed advance, cert_content's
 * lets it through only for pending/in_progress/failed. So an unrecognised
 * status holds here too, and the test is written as "absent or failed" rather
 * than as a list of the states we know about — the list would have to guess at
 * the answer for a state it has never seen, and guess wrong toward the dead
 * end.
 *
 * The cost of keeping them here is a go-and-pay screen shown to someone whose
 * certificate is already issued, in the short window before our automation
 * installs it and hasDgiCredentials releases the guard. Wrong copy on a screen
 * with a working exit beats correct copy on a dead end. Fixing that copy is a
 * separate change, in the step rather than in the routing.
 */
function isRequestHoldingTenantAtAbitab(ctx: OnboardingStepContext): boolean {
  return isCertRequestInFlight(ctx.certificateRequestStatus);
}

/**
 * Pre-DGI cert routing shared by the unsettled-profile and settled-but-cert-
 * pending branches: route to abitab (a Fiscalo cert request is in flight),
 * dgi_access (acq done), cert_content (choice made), or cert_choice (nothing
 * yet), restoring the cert choice from the session marker.
 */
function preDgiCertRouting(
  ctx: OnboardingStepContext,
  at: (step: Step) => number,
): ResolveResult {
  const choice = ctx.certChoiceMarker;
  const r: ResolveResult = { synced: true };
  if (choice === "myself" || choice === "fiscalo") {
    r.choiceSet = true;
    r.choice = choice;
  }
  r.step = isAwaitingAbitab(ctx, { releaseOnAcquisitionDone: true })
    ? at("abitab")
    : ctx.certAcquisitionDoneMarker
      ? at("dgi_access")
      : choice === "myself" || choice === "fiscalo"
        ? at("cert_content")
        : at("cert_choice");
  return r;
}

/**
 * After DGI access is complete: either the cert still needs acquiring (route to
 * the Abitab/cert-content/choice sub-step), or advance through the post-cert
 * tail (cae -> cfe? -> literalE -> personalize -> plan -> done).
 */
function postDgiRouting(
  ctx: OnboardingStepContext,
  at: (step: Step) => number,
): ResolveResult {
  const certAcquisitionDone =
    ctx.hasDgiCredentials || ctx.dgiCertificateSkippedAt;
  const certChoiceMade = ctx.certChoiceMarker;
  const certContentDone = ctx.certAcquisitionDoneMarker;

  // THREE ways out of the certificate phase, not two. The tenant has the
  // certificate (or declared they are not getting one from us); OR a new-cert
  // flow has finished the acquisition screens; OR they chose to keep going
  // while a managed certificate was still in flight.
  //
  // That third exit has to be HERE and not only inside isAwaitingAbitab. For
  // already_issuer — the situation this exists for — isNewCertFlow is false and
  // there are no credentials, so the arm is always entered; suppressing only
  // isAwaitingAbitab drops through to the `certChoiceMade` clause below and
  // lands them on cert_content, which is the exact screen bug 04f1c64 was
  // opened for. See decision 32cd138.
  if (
    !certAcquisitionDone &&
    !(isNewCertFlow(ctx) && certContentDone) &&
    !ctx.certWaitContinuedMarker
  ) {
    // This arm used to return without `synced`, described as "preserved
    // asymmetry" — the Phase-2 refactor transcribed the legacy effect
    // faithfully, bug included, rather than endorsing it. The bug: the page
    // renders a full-screen spinner while !viewStepSynced
    // (onboarding-page.tsx:209), and viewStepSynced is written ONLY from
    // result.synced (useOnboarding.ts:447), starting false. An arm that omits
    // it therefore routes the tenant to the right step and then leaves the
    // page spinning on it forever.
    //
    // It survived because it only bites on a REFRESH. Navigating forward, an
    // earlier pass returns synced: true and the flag sticks — nothing ever
    // sets it back. On a cold load the first pass can land straight here:
    // fiscal profile unsettled + DGI already done returns {} (a deliberate
    // wait that syncs nothing), and the settled pass then arrives in this
    // arm. That is exactly a tenant who has finished DGI and is waiting on a
    // certificate — the state this flow started putting people in today.
    //
    // already_issuer used to be forced down the manual path here — it returned
    // certPathManual, which pinned certificatePath to "manual" and dropped the
    // tenant straight on the upload box. That assumed a migrating tenant holds
    // a working certificate, and they often do not: the old provider may keep
    // it, it may not be exportable, or it may be expired. They now reach
    // cert_choice like every other situation and get asked.
    // A Fiscalo-managed cert request in flight means the tenant's position is
    // the Abitab step, not cert_content — see isAwaitingAbitab. Decision 0a59dad
    // called this case dead in 2026-08, which was true of the flow as it stood
    // then; Fiscalo-managed requests have since made it the common path.
    if (isAwaitingAbitab(ctx, { releaseOnAcquisitionDone: false })) {
      return { step: at("abitab"), synced: true, choiceSet: true, choice: "fiscalo" };
    }
    if (certChoiceMade === "myself" || certChoiceMade === "fiscalo") {
      return { step: at("cert_content"), synced: true, choiceSet: true, choice: certChoiceMade };
    }
    return { step: at("cert_choice"), synced: true, choiceSet: true, choice: null };
  }

  if (
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser &&
    ctx.onboardingMode === "self" &&
    ctx.caeListLoading
  ) {
    return { synced: false };
  }

  const cfeStepResolved = ctx.onboardingCfeImportStepCompletedAt;
  let step: Step | "done";
  if (!ctx.hasRequiredOnboardingCaes) {
    step = "cae";
  } else if (
    ctx.needsCfeImportStep &&
    ctx.ownerPostCertViewSteps.cfe != null &&
    !cfeStepResolved
  ) {
    step = "cfe";
  } else if (!ctx.literalEComplete) {
    step = "literal_e";
  } else if (!ctx.s3Complete) {
    step = "personalize";
  } else if (!ctx.planId) {
    step = "plan";
  } else {
    step = "done";
  }
  return { step: at(step), synced: true };
}
